import React, { useState, useEffect, useRef } from 'react';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { PRINT_ADS, DIGITAL_ADS, CONTACT_PHONE, PAYPAL_CLIENT_ID } from '../config';
import { calcPricing, getRunSchedule, getPrintAdPrice, formatShortDate, toDateInputValue, fmtDollars, getSpecialEditionsInSchedule, getPromoHint } from '../utils/pricing';
import { getDigitalAvailability } from '../utils/availability';
import { buildOrderPayload as buildOrderPayloadFor } from '../utils/orderPayload';
import { currentInAppBrowser, describeBrowser } from '../utils/inAppBrowser';
import { trackEvent, isTestOrder } from '../utils/analytics';
import InAppBrowserNotice from './InAppBrowserNotice';

const NOTIFY_URL = '/.netlify/functions/order-notify';

export default function Step5Review({ form, availability, onRefreshAvailability, onBack, onSwitchToNonprofit, onConfirm, onInvoiceRequested }) {
  const [payError, setPayError] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState('idle'); // 'idle' | 'sending' | 'error'
  // True from the moment PayPal approves until the confirmation page shows.
  // Capturing and booking take a few seconds; on 10/28/26 an advertiser tapped
  // "Email me an invoice" in that gap and was booked twice. The ref blocks a
  // tap in the same instant, before the re-render hides the button.
  const [finishingPayment, setFinishingPayment] = useState(false);
  const finishingPaymentRef = useRef(false);
  const errorAlertSentRef = useRef(false);
  // One reference per visit, so a double-tapped invoice request is only added
  // to the Bookings sheet once.
  const invoiceRefRef = useRef(
    (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : String(Math.random()).slice(2)).replace(/-/g, '').slice(0, 12)
  );
  const inApp = currentInAppBrowser();

  // Re-check live availability when the advertiser reaches this final step, in
  // case a slot filled up while they were filling out the form.
  useEffect(() => { if (onRefreshAvailability) onRefreshAvailability(); }, [onRefreshAvailability]);

  const pricing     = calcPricing(form);
  const runSchedule = getRunSchedule(form.startDate, form.weeks);

  const runScheduleKeys = runSchedule.map(toDateInputValue);
  const digiAvail = getDigitalAvailability(availability, runSchedule, form.digitalSkips || {});
  const conflicts = (form.digitalAds || [])
    .map(id => ({ ad: DIGITAL_ADS.find(a => a.id === id), info: digiAvail[id] }))
    .filter(c => c.ad && c.info && c.info.soldOut);

  const hasPrint    = form.medium === 'print'   || form.medium === 'both';
  const hasDigital  = form.medium === 'digital'  || form.medium === 'both';
  const isNonprofit = form.orgType === 'nonprofit';
  const printAd     = hasPrint && form.printAd ? PRINT_ADS.find(a => a.id === form.printAd) : null;
  const printPrice  = printAd ? getPrintAdPrice(printAd, isNonprofit) : 0;

  const firstDate     = runSchedule[0];
  const lastDate      = runSchedule[runSchedule.length - 1];
  const specialIssues = getSpecialEditionsInSchedule(runSchedule);

  // Only offer the non-profit switch when it would actually lower THIS order's
  // total — some placements (e.g. the email banner) have no non-profit discount,
  // and sending someone on an EIN detour for $0 helps nobody.
  const nonprofitSavings = form.orgType === 'business'
    ? pricing.total - calcPricing({ ...form, orgType: 'nonprofit' }).total
    : 0;

  const promoHint = getPromoHint(pricing);

  const fmtSlot = (ymd) => {
    const [y, m, d] = ymd.split('-').map(Number);
    return formatShortDate(new Date(y, m - 1, d));
  };

  // Shared with the order-notify function, which rebuilds the same order from
  // the raw selections before booking it (see utils/orderPayload.js).
  const buildOrderPayload = (extra) => buildOrderPayloadFor(form, extra);

  // Fallback for anyone PayPal won't work for (usually an in-app browser that
  // blocks the PayPal window). Emails the full order to the ad desk marked
  // UNPAID so it can be invoiced. Unlike the paid-order email, this one must
  // actually arrive, so a failed send is shown to the advertiser.
  async function requestInvoice() {
    if (finishingPaymentRef.current) return;
    setInvoiceStatus('sending');
    try {
      const res = await fetch(NOTIFY_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(buildOrderPayload({ kind: 'invoice_request', browser: describeBrowser(inApp), requestRef: invoiceRefRef.current })),
      });
      if (!res.ok) throw new Error(`order-notify returned ${res.status}`);
      trackEvent(isTestOrder(form, PAYPAL_CLIENT_ID) ? 'ad_test_invoice_request' : 'ad_invoice_request', { value: pricing.total, currency: 'USD', ad_inapp_app: inApp ? (inApp.app || 'unknown') : 'none' });
      onInvoiceRequested({ pricing, runSchedule });
    } catch (err) {
      console.error('Invoice request failed:', err);
      setInvoiceStatus('error');
    }
  }

  // Lets the ad desk follow up with someone PayPal turned away. Sent once per
  // visit so a retry loop can't flood the inbox; never blocks the advertiser.
  function reportPaymentError(err) {
    trackEvent('ad_payment_error', { ad_inapp_app: inApp ? (inApp.app || 'unknown') : 'none' });
    if (errorAlertSentRef.current) return;
    errorAlertSentRef.current = true;
    fetch(NOTIFY_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(buildOrderPayload({
        kind:         'payment_error',
        browser:      describeBrowser(inApp),
        errorMessage: String((err && err.message) || err || '').slice(0, 300),
      })),
    }).catch(e => console.error('Payment-error alert failed:', e));
  }

  return (
    <div className="step-card">
      <h2 className="step-heading">Review &amp; Pay</h2>
      <p className="step-subheading">
        Confirm everything looks right, then pay securely with PayPal.
      </p>

      {/* Placements */}
      <div className="review-section">
        <div className="review-section-title">Selected Placements</div>
        {printAd && (() => {
          const hasOrientations = printAd.specs.length > 1;
          const spec = hasOrientations
            ? (printAd.specs.find(s => s.orientation === form.orientation) || printAd.specs[0])
            : printAd.specs[0];
          const orientationLabel = hasOrientations ? ` (${spec.label})` : '';
          return (
            <div style={{ marginBottom: 6 }}>
              <span className="review-tag">
                Print &mdash; {printAd.label}{orientationLabel}
              </span>
              <div className="review-spec">
                {spec.dims}&ensp;&middot;&ensp;{printAd.format}
              </div>
            </div>
          );
        })()}
        {hasDigital && (form.digitalAds || []).map(id => {
          const ad = DIGITAL_ADS.find(a => a.id === id);
          return ad
            ? <span key={id} className="review-tag">Digital &mdash; {ad.label}</span>
            : null;
        })}
      </div>

      {/* Run dates */}
      <div className="review-section">
        <div className="review-section-title">
          {printAd && printAd.onlyIssue
            ? <>Run date: one issue</>
            : <>Run Dates &mdash; {form.weeks} week{form.weeks !== 1 ? 's' : ''}</>}
        </div>
        <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)' }}>
          {firstDate && lastDate && (
            <>
              {formatShortDate(firstDate)}
              {form.weeks > 1 && <> &rarr; {formatShortDate(lastDate)}</>}
            </>
          )}
        </div>
        {specialIssues.length > 0 && (
          <div style={{ marginTop: 6 }}>
            {specialIssues.map(se => (
              <span key={se.date} className="se-label" style={{ marginRight: 4 }}>
                {se.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Pricing */}
      <div className="review-section">
        <div className="review-section-title">Pricing</div>
        <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)', lineHeight: 1.7 }}>
          {printAd && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span>{printAd.label} (print)</span>
              <span className="nowrap">
                {fmtDollars(printPrice)} &times; {form.weeks}{printAd.onlyIssue ? ' issue' : ''} = {fmtDollars(printPrice * form.weeks)}
              </span>
            </div>
          )}
          {pricing.digitalLines.map(line => {
            const ad = DIGITAL_ADS.find(a => a.id === line.id);
            if (!ad) return null;
            const skipDates = (form.digitalSkips?.[line.id] || []).filter(d => runScheduleKeys.includes(d));
            return (
              <div key={line.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span>
                  {ad.label} (digital)
                  {skipDates.length > 0 && (
                    <span style={{ color: 'var(--charcoal-light)' }}> &mdash; skips {skipDates.map(fmtSlot).join(', ')}</span>
                  )}
                </span>
                <span>
                  {fmtDollars(line.unit)} &times; {line.weeks}{printAd && printAd.onlyIssue ? ` week${line.weeks !== 1 ? 's' : ''}` : ''} = {fmtDollars(line.unit * line.weeks)}
                </span>
              </div>
            );
          })}
          {pricing.discountPerWeek > 0 && (
            <div style={{ color: 'var(--green)', fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
              <span>Multi-week discount</span>
              <span>&minus;{fmtDollars(pricing.totalSavings)}</span>
            </div>
          )}
          {pricing.promoApplies && (
            <div style={{ color: 'var(--green)', fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
              <span>{pricing.promo.label} (${pricing.promoPerWeek}/week)</span>
              <span>&minus;{fmtDollars(pricing.promoSavings)}</span>
            </div>
          )}
        </div>
        {promoHint && <div className="promo-hint">{promoHint}</div>}
      </div>

      {/* Contact */}
      <div className="review-section">
        <div className="review-section-title">Contact</div>
        <div style={{ fontSize: '0.875rem', lineHeight: 1.7 }}>
          <strong>{form.contact.name}</strong> &mdash; {form.contact.businessName}
          <br />
          <span style={{ color: 'var(--charcoal-mid)' }}>
            {form.contact.email} &nbsp;&middot;&nbsp; {form.contact.phone}
          </span>
          <br />
          <span style={{ color: 'var(--charcoal-mid)' }}>
            {form.orgType === 'nonprofit'
              ? <>Non-profit &nbsp;&middot;&nbsp; EIN: {form.ein}</>
              : 'Business / For-profit'}
          </span>
        </div>
        {form.orgType === 'business' && nonprofitSavings > 0 && (
          <button type="button" className="text-link-btn" onClick={onSwitchToNonprofit}>
            Represent a 501(c)(3) non-profit? Switch to discounted rates
          </button>
        )}
      </div>

      <hr className="divider" />

      {/* Grand total */}
      <div className="review-total-box">
        <div className="review-total-label">Grand Total</div>
        <div className="review-total-amount">{fmtDollars(pricing.total)}</div>
      </div>

      {payError && (
        <p className="step-error" style={{ marginBottom: 14 }}>{payError}</p>
      )}

      {conflicts.length > 0 && (
        <div className="availability-warning">
          <div className="availability-warning-title">
            A placement just became unavailable for your dates
          </div>
          <ul>
            {conflicts.map(({ ad }) => (
              <li key={ad.id}><strong>{ad.label}</strong> is now sold out for one or more of your weeks.</li>
            ))}
          </ul>
          <div className="availability-warning-hint">
            Please go back to skip that week, remove the placement, or choose different dates before paying.
          </div>
          <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={onBack}>
            &larr; Back to fix
          </button>
        </div>
      )}

      {!conflicts.length && <InAppBrowserNotice info={inApp} variant="review" />}

      {finishingPayment && (
        <div className="payment-finishing" role="status" aria-live="polite">
          <div className="payment-finishing-title">Finishing your payment&hellip;</div>
          <div className="payment-finishing-hint">This takes a few seconds. Please keep this page open.</div>
        </div>
      )}

      {/* Hidden, not removed, while a payment finishes: unmounting the PayPal
          buttons mid-capture could cut the capture off. */}
      <div className="paypal-wrapper" style={conflicts.length || finishingPayment ? { display: 'none' } : undefined}>
        <PayPalButtons
          style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }}
          createOrder={(data, actions) =>
            actions.order.create({
              purchase_units: [{
                amount: {
                  value:         pricing.total.toFixed(2),
                  currency_code: 'USD',
                },
                description: `Hendersonville Lightning Ad — ${form.contact.businessName}`,
              }],
              application_context: {
                brand_name: 'Hendersonville Lightning',
                shipping_preference: 'NO_SHIPPING',
              },
            })
          }
          onApprove={(data, actions) => {
            finishingPaymentRef.current = true;
            setFinishingPayment(true);
            setPayError('');
            return actions.order.capture().then(async (details) => {
              // Fire order notification email — non-blocking.
              // If the function fails, we still show confirmation (payment already captured).
              try {
                await fetch(NOTIFY_URL, {
                  method:  'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body:    JSON.stringify(buildOrderPayload({ paypalTransactionId: details.id })),
                });
              } catch (err) {
                // Log only — do not block confirmation.
                console.error('Notification email failed:', err);
              }

              onConfirm({ details, pricing, runSchedule });
            }, (err) => {
              // Capture failed, so nothing was charged: bring the payment and
              // invoice options back, then let PayPal's onError report it.
              finishingPaymentRef.current = false;
              setFinishingPayment(false);
              throw err;
            });
          }}
          onError={err => {
            console.error('PayPal error', err);
            reportPaymentError(err);
            setPayError(
              'Payment could not be completed. Please try again, or tap "Email me an invoice" below and we\'ll send you one.'
            );
          }}
          onCancel={() => setPayError('')}
        />
      </div>

      {!conflicts.length && !finishingPayment && (
        <div className="invoice-fallback">
          <div className="invoice-fallback-lead">Having trouble paying?</div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={requestInvoice}
            disabled={invoiceStatus === 'sending'}
          >
            {invoiceStatus === 'sending' ? 'Sending\u2026' : 'Email me an invoice'}
          </button>
          <div className="invoice-fallback-hint">
            We'll send an invoice to {form.contact.email || 'your email'} that you can pay by card.
          </div>
          {invoiceStatus === 'error' && (
            <p className="step-error">
              We couldn't send your request. Please call <a href={CONTACT_PHONE.tel}>{CONTACT_PHONE.display}</a> or
              email <a href="mailto:ads@hendersonvillelightning.com">ads@hendersonvillelightning.com</a>.
            </p>
          )}
        </div>
      )}

      <div className="nav-buttons" style={{ marginTop: 18 }}>
        <button className="btn btn-secondary" onClick={onBack} disabled={finishingPayment}>&larr; Back</button>
        <span style={{ fontSize: '0.75rem', color: 'var(--charcoal-light)', textAlign: 'right', maxWidth: 240 }}>
          Payments are processed securely by PayPal.
        </span>
      </div>
    </div>
  );
}
